import { CircleCheckBig } from 'lucide-react'
import { toast as sonner } from 'sonner'
import type { ExternalToast } from 'sonner'

/**
 * Tag mapping's toasts, positioned and iconed where Figma draws them.
 *
 * Two deviations from the app default, both taken per-toast (sonner accepts
 * `position` and `icon` on every call) rather than by editing the shared
 * component, so nothing outside this route moves:
 *
 * 1. POSITION. The app mounts one `<Toaster position="bottom-center">`
 *    (main.tsx), but Figma section "11. Edit" puts the confirmation at the TOP
 *    -- "Mapping updated successfully" (1232:285869) and "Approved mapping
 *    successfully" (1232:286163) both sit centred on x=720 of the 1440 frame,
 *    ~42px down.
 *
 * 2. ICON. `IntuiToast` renders `CheckCircle2` for a success, against its own
 *    Figma node (193:1386). These frames draw `CircleCheckBig` -- the check
 *    breaks the circle rather than sitting inside it. The two nodes disagree;
 *    the shared component keeps following its own spec and this route follows
 *    the frame it was drawn from. If design settles it the other way, delete
 *    the icon here rather than leaving both.
 *
 * Wrapped rather than passed at each call site so the whole route moves
 * together: a success at the top and its matching error at the bottom is worse
 * than either choice made consistently. Errors take the position but keep the
 * shared error glyph -- the frames draw no error state to copy.
 */
const AT_TOP = {
  position: 'top-center',
  /**
   * Hug the text. Sonner sizes every toast to a fixed `--width` (356px), which
   * left a visible run of empty space after "Mapping deleted successfully" —
   * Figma 12 (1304:29552) draws the confirmation sized to its own content.
   * Inline, so it beats the stylesheet's `width: var(--width)` without changing
   * the shared Toaster for the rest of the app. The cap keeps a long message
   * from spanning the viewport.
   */
  style: { width: 'fit-content', maxWidth: 'min(90vw, 420px)' },
} as const

/** 20px, matching IntuiToast's own icon sizing. */
const SuccessIcon = <CircleCheckBig className="h-5 w-5" />

export const toast = {
  success: (message: string, options?: ExternalToast) =>
    sonner.success(message, { ...AT_TOP, icon: SuccessIcon, ...options }),
  error: (message: string, options?: ExternalToast) =>
    sonner.error(message, { ...AT_TOP, ...options }),
}
